import { useMutation } from '@apollo/client'
import { FormControl, Select, TextField } from '@mui/material'
import { Field, Form, Formik } from 'formik'

import { useNavigate } from 'react-router-dom'

import { FloraButton, Flex, FloraTypography } from '@grupoboticario/flora-react'

import FormInfo from '@/components/FormInfo/FormInfo'
import { STATUS, STATUS_DETAILS, STATUS_REASON } from '@/constants/status'
import { useHeaderContext } from '@/context/header'
import { useUserContext } from '@/context/user'
import PATCH_MEMBER from '@/graphql/mutation/member'
import PATCH_MEMBER_STATUS from '@/graphql/mutation/memberStatus'
import { Actions } from '@/utils/dictionaries/actionsDictionary/actions'
import { translateMemberStatusReason } from '@/utils/format/translateMemberStatusReason'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'
import { deactivateInput } from '@/utils/validators/deactivateUserInputs'

import { DEFAULT_STATUS_REASON } from './constants'
import * as S from './StatusForm.styles'

const StatusForm = ({ data: memberData, documentNumber, onStatusChange = () => {} }) => {
  const [editMember, { loading }] = useMutation(PATCH_MEMBER, {
    onError: () => {}
  })
  const [editMemberStatus] = useMutation(PATCH_MEMBER_STATUS, {
    onError: () => {}
  })
  const { user, hasPermission } = useUserContext()
  const { setMemberStatus, memberStatus } = useHeaderContext()
  const navigate = useNavigate()

  const updateMember = async (member) => {
    const { error, errors } = await editMember({
      variables: {
        documentNumber,
        member: {
          status: member.status,
          origin: member.origin,
          statusChange: {
            ...member.statusChange,
            reason: member?.statusChange?.reason || undefined,
            responsible: user.email
          },
          email: member.email,
          documentNumber
        }
      }
    })

    const { statusError, statusErrors } = await editMemberStatus({
      variables: {
        documentNumber,
        memberStatus: {
          status: member.status,
          statusReason: member?.statusChange?.reason || undefined,
          statusChange: {
            text: member?.statusChange?.text || undefined,
            responsible: user.email,
            reason: member?.statusChange?.reason || undefined
          }
        }
      }
    })

    triggerToastSuccess(
      error || errors || statusError || statusErrors,
      'Participante modificado com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.'
    )

    if (!statusError || !statusErrors)
      setMemberStatus({ status: member.status, isBlocked: member.status === STATUS.BLOCKED })
    onStatusChange()
  }

  const canSelectStatus = (status) =>
    ({
      [STATUS.ACTIVE]: !hasPermission(Actions.EDIT_STATUS_BASIC),
      [STATUS.INACTIVE]: !hasPermission(Actions.EDIT_STATUS_BASIC),
      [STATUS.MIGRATED]: !hasPermission(Actions.SELECT_STATUS_MIGRATED),
      [STATUS.BLOCKED]: !hasPermission(Actions.SELECT_STATUS_BLOCKED)
    })[status] || false

  const canUnblockFraud = (status, statusReason) =>
    !(
      status === STATUS.BLOCKED &&
      statusReason === STATUS_REASON.FRAUD &&
      !hasPermission(Actions.EDIT_STATUS_BLOCKED_FRAUD)
    )

  const canChangeStatus = (status, statusReason) =>
    canUnblockFraud(status, statusReason) &&
    ({
      [STATUS.ACTIVE]: hasPermission(Actions.EDIT_STATUS_BASIC),
      [STATUS.INACTIVE]: hasPermission(Actions.EDIT_STATUS_BASIC),
      [STATUS.MIGRATED]: hasPermission(Actions.EDIT_STATUS_MIGRATED),
      [STATUS.BLOCKED]: hasPermission(Actions.EDIT_STATUS_BLOCKED)
    }[status] ||
      false)

  const deactivateInputCanChangeStatus = deactivateInput(
    canChangeStatus(memberData?.status, memberData?.statusChange?.reason),
    memberStatus?.status,
    memberData?.statusChange
  )

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...memberData,
        statusChange: { ...memberData.statusChange, text: '' }
      }}
      onSubmit={updateMember}
    >
      {({ values }) => {
        const hasChange =
          memberData?.status !== values?.status || memberData?.statusChange?.reason !== values?.statusChange?.reason

        return (
          <Form>
            <Flex direction="column" gap="$5">
              <Flex gap="$5">
                <FormControl style={{ width: '90%' }} variant="outlined">
                  <FormInfo title="Status:" />
                  <Field
                    as={Select}
                    disabled={deactivateInputCanChangeStatus}
                    inputProps={{ id: 'status' }}
                    name="status"
                    native
                    required
                    style={{ height: '48px' }}
                  >
                    {Object.values(STATUS_DETAILS)?.map(({ status, label }) => (
                      <option disabled={canSelectStatus(status)} key={status} value={status}>
                        {label}
                      </option>
                    ))}
                  </Field>
                </FormControl>
                <FormControl style={{ width: '100%' }} variant="outlined">
                  <FormInfo title="Motivo:" />
                  <Field
                    as={Select}
                    disabled={deactivateInputCanChangeStatus}
                    inputProps={{ id: 'statusReason' }}
                    name="statusChange.reason"
                    native
                    required={values.status === 'INACTIVE' || values.status === 'BLOCKED'}
                    style={{ height: '48px' }}
                  >
                    <option value="">Selecione</option>
                    {DEFAULT_STATUS_REASON?.map(
                      (reason) =>
                        reason && (
                          <option key={reason} value={reason}>
                            {translateMemberStatusReason[reason] || `Razão desconhecida (${reason})`}
                          </option>
                        )
                    )}
                  </Field>
                </FormControl>
              </Flex>
              <Flex css={S.fullWidthCss} direction="column">
                <FormInfo title="Justificativa da alteração:" />
                {hasChange ? (
                  <FormControl>
                    <Field as={TextField} multiline name="statusChange.text" required rows={6} variant="outlined" />
                  </FormControl>
                ) : (
                  <>
                    <Field
                      as={TextField}
                      disabled
                      multiline
                      name="statusChange.text"
                      rows={2}
                      value={memberData?.statusChange?.text}
                      variant="outlined"
                    />
                    <FloraTypography fontSize="exceptionsAuxiliar" css={S.justificationNoteCss}>
                      Última alteração de status realizada por: {memberData?.statusChange?.responsible || '-'}{' '}
                    </FloraTypography>
                  </>
                )}
              </Flex>
              <Flex gap="$5" justify="flex-end" wrap="wrap">
                <FloraButton
                  css={S.responsiveButtonCss}
                  isUppercase
                  onClick={(e) => {
                    e.preventDefault()
                    navigate(-1)
                  }}
                  hierarchy="secondary"
                >
                  Cancelar Alterações
                </FloraButton>
                <FloraButton
                  css={S.responsiveButtonCss}
                  disabled={
                    !hasChange || deactivateInput(hasPermission(Actions.UPDATE_REGISTER_DATA), memberStatus?.status)
                  }
                  isFull
                  isLoading={loading}
                  isUppercase
                  type="submit"
                >
                  Salvar Alterações
                </FloraButton>
              </Flex>
            </Flex>
          </Form>
        )
      }}
    </Formik>
  )
}

export default StatusForm
